import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { logout } from "../api/auth";
import { getCurrentUser } from "../api/users";
import "../styles/Navbar.css";
import { getHomeRoute, hasRight, readStoredUser } from "../utils/routing";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [fullName, setFullName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [greeting, setGreeting] = useState("Hello");
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [currentUser, setCurrentUser] = useState(readStoredUser());

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const data = await getCurrentUser();

        // Save user locally for RBAC routing
        localStorage.setItem("user", JSON.stringify(data));
        setCurrentUser(data);

        setFullName(
          data.name ||
            data.username ||
            data.first_name ||
            "Guest"
        );

        // Determine role display
        if ((data.access_rights || []).length > 1) {
          setUserRole(`${data.access_rights.length} access rights`);
        } else if (data.approver_level === 1) {
          setUserRole("L1 Approver");
        } else if (data.approver_level === 2) {
          setUserRole("L2 Approver");
        } else if (data.role === "admin" || data.is_admin) {
          setUserRole("Administrator");
        } else if (data.role === "finance") {
          setUserRole("Finance");
        } else if (data.role === "stores") {
          setUserRole("Stores");
        } else {
          setUserRole("Requester");
        }
      } catch (err) {
        console.error("Error fetching current user:", err);
        setFullName("Guest");
        setUserRole("Guest");
      }
    };

    fetchUser();

    const hour = new Date().getHours();

    if (hour < 12) {
      setGreeting("Good morning");
    } else if (hour < 18) {
      setGreeting("Good afternoon");
    } else {
      setGreeting("Good evening");
    }
  }, []);

  const handleLogout = () => {
    logout();

    localStorage.removeItem("token");
    localStorage.removeItem("user");

    navigate("/login");
  };

  const getInitials = (name) => {
    if (!name) return "?";

    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleColor = (role) => {
    if (role.includes("L1")) return "#a92f6f";
    if (role.includes("L2")) return "#a9ae50";
    if (role.includes("Admin")) return "#0ea5e9";
    if (role.includes("Finance")) return "#059669";
    if (role.includes("Stores")) return "#ea580c";

    return "#6b7280";
  };

  return (
    <>
      <nav className="navbar">
        {/* LEFT */}
        <div className="nav-left">
          {/* LOGO */}
          <div
            className="nav-brand"
            onClick={() => navigate(getHomeRoute())}
          >
            <div className="brand-icon">
              <svg
                width="28"
                height="28"
                viewBox="0 0 28 28"
                fill="none"
              >
                <rect
                  width="28"
                  height="28"
                  rx="7"
                  fill="#a92f6f"
                />

                <path
                  d="M8 14L12 18L20 10"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <div className="brand-text">
              <span className="brand-name">
                R-Requit
              </span>

              <span className="brand-tag">
                Purchase Workflow
              </span>
            </div>
          </div>

          {/* NAVIGATION */}
          <div className="nav-links">
            {/* HOME */}
            <button
              className={`nav-link ${
                location.pathname !== "/dashboard"
                  ? "active"
                  : ""
              }`}
              onClick={() =>
                navigate(getHomeRoute())
              }
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 9.5L12 3l9 6.5" />
                <path d="M5 10v10h14V10" />
              </svg>

              Home
            </button>

            {[
              ["request", "/requester", "Requests"],
              ["approve", "/approver", "Approvals"],
              ["finance", "/finance", "Finance"],
              ["stores", "/stores", "Stores"],
              ["admin", "/admin", "Admin"],
            ].filter(([right]) => hasRight(currentUser, right)).map(([right, path, label]) => (
              <button key={right} className={`nav-link ${location.pathname === path ? "active" : ""}`} onClick={() => navigate(path)}>
                {label}
              </button>
            ))}

            {/* DASHBOARD */}
            <button
              className={`nav-link ${
                location.pathname === "/dashboard"
                  ? "active"
                  : ""
              }`}
              onClick={() =>
                navigate("/dashboard")
              }
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect
                  x="3"
                  y="3"
                  width="7"
                  height="7"
                  rx="1"
                />
                <rect
                  x="14"
                  y="3"
                  width="7"
                  height="7"
                  rx="1"
                />
                <rect
                  x="14"
                  y="14"
                  width="7"
                  height="7"
                  rx="1"
                />
                <rect
                  x="3"
                  y="14"
                  width="7"
                  height="7"
                  rx="1"
                />
              </svg>

              Dashboard
            </button>
          </div>
        </div>

        {/* RIGHT */}
        <div className="nav-right">
          {/* GREETING */}
          <span className="greeting-text">
            {greeting},{" "}
            <strong>
              {fullName.split(" ")[0]}
            </strong>
          </span>

          {/* USER MENU */}
          <div className="user-menu">
            <button
              className="user-trigger"
              onClick={() =>
                setShowUserMenu(!showUserMenu)
              }
              style={{
                borderColor:
                  getRoleColor(userRole) + "30",
              }}
            >
              <div
                className="user-avatar"
                style={{
                  background: getRoleColor(userRole),
                }}
              >
                {getInitials(fullName)}
              </div>

              <div className="user-info">
                <span className="user-name">
                  {fullName}
                </span>

                <span
                  className="user-role"
                  style={{
                    color: getRoleColor(userRole),
                  }}
                >
                  {userRole}
                </span>
              </div>

              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                className={`chevron ${
                  showUserMenu ? "open" : ""
                }`}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {showUserMenu && (
              <div className="user-dropdown">
                {/* USER HEADER */}
                <div className="dropdown-header">
                  <div
                    className="dropdown-avatar"
                    style={{
                      background:
                        getRoleColor(userRole),
                    }}
                  >
                    {getInitials(fullName)}
                  </div>

                  <div className="dropdown-info">
                    <span className="dropdown-name">
                      {fullName}
                    </span>

                    <span
                      className="dropdown-role"
                      style={{
                        color:
                          getRoleColor(userRole),
                      }}
                    >
                      {userRole}
                    </span>
                  </div>
                </div>

                <div className="dropdown-divider" />

                {/* HOME */}
                <button
                  className="dropdown-item"
                  onClick={() =>
                    navigate(getHomeRoute())
                  }
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 9.5L12 3l9 6.5" />
                    <path d="M5 10v10h14V10" />
                  </svg>

                  Home
                </button>

                {/* DASHBOARD */}
                <button
                  className="dropdown-item"
                  onClick={() =>
                    navigate("/dashboard")
                  }
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="3"
                      y="3"
                      width="7"
                      height="7"
                      rx="1"
                    />
                    <rect
                      x="14"
                      y="3"
                      width="7"
                      height="7"
                      rx="1"
                    />
                    <rect
                      x="14"
                      y="14"
                      width="7"
                      height="7"
                      rx="1"
                    />
                    <rect
                      x="3"
                      y="14"
                      width="7"
                      height="7"
                      rx="1"
                    />
                  </svg>

                  Dashboard
                </button>

                {/* LOGOUT */}
                <button
                  className="dropdown-item logout-btn"
                  onClick={handleLogout}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />

                    <polyline points="16 17 21 12 16 7" />

                    <line
                      x1="21"
                      y1="12"
                      x2="9"
                      y2="12"
                    />
                  </svg>

                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* INFO BAR */}
      <div className="info-bar">
        <span className="info-text">
          Streamlining your purchasing process
          for efficiency and transparency.
        </span>
      </div>
    </>
  );
};

export default Navbar;
